// Slider.tsx
import React, { FunctionComponent, useState } from "react";
import styled from "styled-components";
import { Slider as MUISlider } from "@mui/material";
import Colors from "../global/Colors";
import Fonts from "../global/Fonts";

const SliderRoot = styled.div<{ $disabled: boolean; $orientation: 'horizontal' | 'vertical' }>`
    position: relative;
    display: flex;
    flex-direction: ${props => props.$orientation === 'vertical' ? 'column' : 'row'};
    align-items: center;
    justify-content: center;
    width: ${props => props.$orientation === 'vertical' ? 'auto' : '100%'};
    height: ${props => props.$orientation === 'vertical' ? '100%' : 'auto'};
    padding: ${props => props.$orientation === 'vertical' ? '6px 0' : '0 6px'};
    overflow: visible;
    pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
`;

const StyledSlider = styled(MUISlider)<{ 
  $colorMode?: 'light' | 'dark'; 
  $disabled: boolean; 
  $size?: 'default' | 'small';
  $hideKnob: boolean;
  $isHovering: boolean;
  $miniKnob?: boolean;
}>`
  && {
    max-width: 100%;
    box-sizing: border-box;
    
    .MuiSlider-rail {
      height: ${props => props.$size === 'small' ? '3px' : '4px'};
      background-color: ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey2};
      opacity: 1;
    }
    
    .MuiSlider-track {
      height: ${props => props.$size === 'small' ? '3px' : '4px'};
      background-color: ${props => props.$colorMode === 'dark' ? Colors.grey5 : Colors.grey4};
      border: none;
      transition: none;
    }
    
    .MuiSlider-thumb {
      width: ${props => props.$miniKnob ? '6px' : (props.$size === 'small' ? '12px' : '16px')};
      height: ${props => props.$miniKnob ? '12px' : (props.$size === 'small' ? '12px' : '16px')};
      border-radius: ${props => props.$miniKnob ? '3px' : '50%'};
      background-color: ${props => props.$disabled ? Colors.grey5 : (props.$colorMode === 'dark' ? Colors.grey3 : Colors.grey6)};
      box-shadow: none;
      opacity: ${props => (props.$hideKnob && !props.$isHovering) ? '0' : '1'};
      transition: opacity 0.2s ease;
      
      &:hover, &.Mui-focusVisible {
        background-color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.grey7};
        box-shadow: ${props => (props.$size === 'small' || props.$miniKnob) ? 'none' : `0 0 0 6px ${props.$colorMode === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.16)'}`};
      }
      
      &.Mui-active {
        background-color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.grey7};
        box-shadow: ${props => (props.$size === 'small' || props.$miniKnob) ? 'none' : `0 0 0 8px ${props.$colorMode === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.16)'}`};
      }
    }
    
    .MuiSlider-mark {
      width: ${props => props.$size === 'small' ? '3px' : '4px'};
      height: ${props => props.$size === 'small' ? '3px' : '4px'};
      border-radius: 50%;
      background-color: ${props => props.$colorMode === 'dark' ? Colors.grey4 : Colors.grey4};
      opacity: 0.7;
    }
    
    .MuiSlider-markActive {
      background-color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.grey8};
      opacity: 1;
    }
    
    .MuiSlider-valueLabel {
      background-color: ${props => props.$colorMode === 'dark' ? Colors.black : Colors.white};
      color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.black};
      font-family: ${Fonts.lexendMedium.fontFamily};
      font-weight: ${Fonts.lexendMedium.fontWeight};
      font-size: ${props => props.$size === 'small' ? '12px' : '14px'};
      border-radius: 6px;
      // padding: 2px 8px;
      border: 1px solid ${props => props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey2};
      box-shadow: 0px 2px 4px ${props => props.$colorMode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
    }
  }
`;

export interface SliderProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  tick?: boolean;
  colorMode?: 'dark' | 'light';
  disabled?: boolean;
  size?: 'default' | 'small';
  onChange: (value: number) => void;
  marks?: boolean | { value: number; label?: string }[];
  showLabel?: boolean;
  hideKnobUntilHover?: boolean;
  miniKnob?: boolean;
  orientation?: 'horizontal' | 'vertical';
}

const Slider: FunctionComponent<SliderProps> = ({
  value,
  min,
  max,
  colorMode = 'light',
  disabled = false,
  onChange,
  step,
  marks,
  showLabel = true,
  size = 'default',
  hideKnobUntilHover = false,
  miniKnob = false,
  orientation = 'horizontal'
}) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <SliderRoot 
      $disabled={disabled}
      $orientation={orientation}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <StyledSlider
        value={value}
        min={min}
        max={max}
        step={step}
        marks={marks}
        $colorMode={colorMode}
        disabled={disabled}
        onChange={(_, newValue) => onChange(newValue as number)}
        valueLabelDisplay={showLabel ? "auto" : "off"}
        $disabled={disabled}
        $size={size}
        $hideKnob={hideKnobUntilHover}
        $isHovering={isHovering}
        $miniKnob={miniKnob}
        orientation={orientation}
      />
    </SliderRoot>
  );
};

export default Slider;